import { useState } from "react";
import { Link } from "react-router";
import { PhoneNav } from "../../shared/phone/PhoneNav";
import { Sheet } from "../../shared/phone/Sheet";
import type { PartnerHomeActivity, PartnerHomeRange, PartnerHomeResponse } from "../../shared/types";
import { usePartnerAuth } from "../context/PartnerAuthContext";
import { count, money, shortDate } from "../lib/format";
import { Icon } from "../components/phone/Icon";
import { EmptyBlock, PoweredBy, ProgramsSheet, ProgramTile, RangeButton } from "../components/phone/parts";
import { toast } from "../components/phone/Toast";

const RANGES: { key: PartnerHomeRange; label: string }[] = [
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "90d", label: "Last 90 days" },
  { key: "month", label: "This month" },
  { key: "all", label: "All time" },
];

async function copyText(value: string, done: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(value);
    toast(done, "copy");
  } catch {
    toast("Copying is blocked here. Press and hold to copy.", "info");
  }
}

/** The program's name as the large title: tap it to switch programs. */
function ProgramTitle({ name, onOpen }: { name: string; onOpen: () => void }) {
  return (
    <button type="button" className="po-title" onClick={onOpen} aria-label={`${name}, switch program`}>
      <ProgramTile name={name} size={30} />
      <span>{name}</span>
      <Icon name="chevD" />
    </button>
  );
}

/** A dollar ceiling for the chart that reads well: 1, 2, 2.5 or 5 times a power of ten. */
function niceTop(maxCents: number): number {
  const dollars = Math.max(1, maxCents / 100);
  const pow = 10 ** Math.floor(Math.log10(dollars));
  const step = [1, 2, 2.5, 5, 10].find((s) => s * pow >= dollars) ?? 10;
  return Math.max(100, step * pow) * 100;
}

function Earnings({ home }: { home: PartnerHomeResponse }) {
  const pts = home.earnings;
  const [pick, setPick] = useState(pts.length - 1);
  const top = niceTop(Math.max(0, ...pts.map((p) => p.cents)));
  const total = pts.reduce((a, p) => a + p.cents, 0);
  const at = pts[Math.min(pick, pts.length - 1)];
  const grid = [0, top / 2, top];
  return (
    <section>
      <div className="pn-sh">
        <h2>Earnings</h2>
        <span className="po-shn">
          {money(total, home.currency)} in {pts.length} months
        </span>
      </div>
      <div className="pn-card po-chart">
        {at ? (
          <div className="po-read">
            <b>{money(at.cents, home.currency)}</b>
            <span>in {at.label}</span>
          </div>
        ) : null}
        <div className="po-bars" style={{ gridTemplateColumns: `repeat(${pts.length}, minmax(0, 1fr))` }}>
          {grid.map((g) => (
            <span key={g} className={g ? "po-gl" : "po-gl base"} style={{ bottom: `${24 + (g / top) * 120}px` }}>
              <b>{money(g, home.currency)}</b>
              <i />
            </span>
          ))}
          {pts.map((p, i) => (
            <button
              key={`${p.label}-${i}`}
              type="button"
              className={["po-bar", i === pick ? "on" : "", p.cents ? "" : "zero"].filter(Boolean).join(" ")}
              onClick={() => setPick(i)}
              aria-label={`${p.label}, ${money(p.cents, home.currency)}`}
              aria-pressed={i === pick}
            >
              <span className="po-c">
                <i style={{ height: p.cents ? `${Math.max(3, (p.cents / top) * 120)}px` : "2px" }} />
              </span>
              <span className="po-bl">{p.label}</span>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

const TONE: Record<PartnerHomeActivity["tone"], { icon: "plus" | "clock" | "check"; cls: string }> = {
  pending: { icon: "plus", cls: "pn-ico" },
  held: { icon: "clock", cls: "pn-ico warn" },
  earned: { icon: "check", cls: "pn-ico good" },
  paid: { icon: "check", cls: "pn-ico good" },
};

function activityAmount(a: PartnerHomeActivity, currency: string): { text: string; cls: string } {
  if (a.amountCents === null) return { text: "pending", cls: "po-amt dim" };
  const sign = a.tone === "earned" || a.tone === "held" ? "+" : "";
  return { text: `${sign}${money(a.amountCents, currency)}`, cls: a.tone === "paid" || a.tone === "earned" ? "po-amt good" : "po-amt" };
}

function activitySub(a: PartnerHomeActivity): string {
  if (a.kind === "payout") return a.detail;
  const what = a.detail ? a.detail[0].toUpperCase() + a.detail.slice(1) : "";
  return [what, shortDate(a.at)].filter(Boolean).join(", ");
}

/**
 * Home on a phone and an iPad: the program's name as the large title (tap to switch), the partner's link
 * and coupon first, then the numbers, earnings by month and what happened lately. Notices to re-sign or
 * about approval come first, as full-width banners.
 */
export function HomeNative({
  home,
  error,
  range,
  onRange,
  dismissed,
  onDismiss,
  onRetry,
}: {
  home: PartnerHomeResponse | null;
  error: string | null;
  range: PartnerHomeRange;
  onRange: (r: PartnerHomeRange) => void;
  dismissed: boolean;
  onDismiss: () => void;
  onRetry: () => void;
}) {
  const { currentMembership } = usePartnerAuth();
  const [switching, setSwitching] = useState(false);
  const [ranging, setRanging] = useState(false);
  const name = home?.productName ?? currentMembership?.productName ?? "Home";
  const title = <ProgramTitle name={name} onOpen={() => setSwitching(true)} />;
  const rangeLabel = (RANGES.find((r) => r.key === (home?.range ?? range)) ?? RANGES[1]).label;

  let body;
  if (error && !home) {
    body = (
      <div className="pn-stack">
        <EmptyBlock icon="info" title="Something went wrong">
          {error}
        </EmptyBlock>
        <div className="pn-pad">
          <button type="button" className="pn-btn soft wide" onClick={onRetry}>
            Try again
          </button>
        </div>
      </div>
    );
  } else if (!home) {
    body = <div className="po-loading" role="status" aria-label="Loading" />;
  } else {
    const showPending = home.status === "pending" && !dismissed;
    const linkShown = home.link ? home.link.replace(/^https?:\/\//, "") : `/go?p=${home.linkSlug}`;
    body = (
      <div className="pn-stack po-home po-wide">
        {currentMembership?.needsResign ? (
          <div className="po-ban warn" role="status">
            <span className="po-ban-i">
              <Icon name="file" />
            </span>
            <p>
              <b>{home.productName} updated the partner terms.</b> Re-sign to keep your link active.
            </p>
            <Link className="pn-btn pri" to={`/onboarding/${currentMembership.programId}`}>
              Review and re-sign
            </Link>
          </div>
        ) : null}
        {showPending ? (
          <div className="po-ban info" role="status">
            <span className="po-ban-i">
              <Icon name="clock" />
            </span>
            <p>
              <b>Pending {home.productName}'s approval.</b> Your link works now. Rewards unlock once you are approved.
            </p>
            <button type="button" className="po-ban-x" onClick={onDismiss} aria-label="Dismiss">
              <Icon name="x" />
            </button>
          </div>
        ) : null}

        <div className="po-duo">
          <section className="pn-card po-link">
            <div className="po-k">Your link</div>
            <div className="po-url">
              <Icon name="link" />
              <span>{linkShown}</span>
            </div>
            {home.link ? (
              <button type="button" className="pn-btn pri wide" onClick={() => home.link && void copyText(home.link, "Link copied")}>
                <Icon name="copy" />
                Copy link
              </button>
            ) : (
              <p className="po-note">Your link works once {home.productName} sets where it goes.</p>
            )}
            {home.couponCode ? (
              <>
                <div className="po-k po-k2">
                  Your coupon
                  <span>
                    {home.couponDiscountPercent
                      ? `Your audience saves ${home.couponDiscountPercent}%, you still earn`
                      : "Your audience saves, you still earn"}
                  </span>
                </div>
                <div className="po-cpn">
                  <span className="po-code">{home.couponCode}</span>
                  <button type="button" className="pn-btn soft" onClick={() => home.couponCode && void copyText(home.couponCode, "Coupon copied")}>
                    Copy
                  </button>
                </div>
              </>
            ) : null}
            <p className="po-disc">
              When you post, add <b>#ad</b> or <b>paid link</b> so people know you earn a commission.
            </p>
          </section>

          {home.hasActivity ? (
            <section>
              <div className="pn-sh po-sh">
                <h2>Numbers</h2>
                <RangeButton label={rangeLabel} onClick={() => setRanging(true)} />
              </div>
              <div className="po-stats">
                <div className="po-st">
                  <span className="l">Clicks</span>
                  <span className="n">{count(home.kpis.clicks)}</span>
                </div>
                <div className="po-st">
                  <span className="l">Signed up</span>
                  <span className="n">{count(home.kpis.signups)}</span>
                  {home.signupsThisWeek > 0 ? <span className="s up">+{count(home.signupsThisWeek)} this week</span> : null}
                </div>
                <div className="po-st">
                  <span className="l">Customers</span>
                  <span className="n">{count(home.kpis.customers)}</span>
                </div>
                <div className="po-st">
                  <span className="l">Earned</span>
                  <span className="n">{money(home.kpis.earnedCents, home.currency)}</span>
                  {home.earnedThisMonthCents > 0 ? (
                    <span className="s up">+{money(home.earnedThisMonthCents, home.currency)} this month</span>
                  ) : null}
                </div>
              </div>
            </section>
          ) : (
            <EmptyBlock icon="chart" title="No activity yet">
              Share your link or coupon above. Your clicks, sign-ups and rewards show up here as they come in.
            </EmptyBlock>
          )}
        </div>

        {home.hasActivity ? (
          <div className="po-duo">
            <Earnings home={home} />
            <section>
              <div className="pn-sh">
                <h2>Recent</h2>
              </div>
              {home.recent.length === 0 ? (
                <p className="po-none">Nothing yet. New activity lands here.</p>
              ) : (
                <div className="pn-grp">
                  {home.recent.map((a) => {
                    const amt = activityAmount(a, home.currency);
                    const tone = TONE[a.tone];
                    return (
                      <div className="pn-row static" key={a.id}>
                        <span className={tone.cls}>
                          <Icon name={tone.icon} />
                        </span>
                        <span className="m">
                          <span className="t">{a.title}</span>
                          <span className="s">{activitySub(a)}</span>
                        </span>
                        <span className={`v ${amt.cls}`}>{amt.text}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        ) : null}
        <PoweredBy />
      </div>
    );
  }

  return (
    <div className="pn-screen">
      <PhoneNav large title={title} />
      {body}
      <ProgramsSheet open={switching} onClose={() => setSwitching(false)} />
      <Sheet open={ranging} onClose={() => setRanging(false)} title="Show numbers for">
        <div className="pn-grp">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              className="pn-row"
              onClick={() => {
                setRanging(false);
                onRange(r.key);
              }}
            >
              <span className="m">
                <span className="t">{r.label}</span>
              </span>
              {r.key === (home?.range ?? range) ? <Icon name="check" className="po-tick" /> : null}
            </button>
          ))}
        </div>
      </Sheet>
    </div>
  );
}
